import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import tippy, { type Instance } from 'tippy.js';

import MentionSuggestionList, {
  type MentionSuggestionItem,
  type MentionSuggestionListRef,
} from './MentionSuggestionList';

export type MentionSuggestionCallbacks = {
  /** Fires when the @-mention dropdown opens (true) or closes (false). Used to avoid Enter-to-submit while choosing a mention. */
  onActiveChange?: (active: boolean) => void;
};

/**
 * Creates the Tiptap mention suggestion configuration.
 *
 * @param getEmployees - async function that searches employees by query string.
 *                       Returns array of { id, label, avatarUrl? }.
 * @param aiEnabled - whether AI Agent should appear in suggestions.
 * @param callbacks - optional hooks (e.g. track when the suggestion UI is open).
 */
export function createMentionSuggestion(
  getEmployees: (query: string) => Promise<MentionSuggestionItem[]>,
  aiEnabled: boolean,
  aiAgentLabel: string,
  callbacks?: MentionSuggestionCallbacks
): Omit<SuggestionOptions<MentionSuggestionItem, MentionNodeAttrs>, 'editor'> {
  const aiAgentItem: MentionSuggestionItem = {
    id: 'ai-agent',
    label: aiAgentLabel,
    isAiAgent: true,
  };

  return {
    items: async ({ query }): Promise<MentionSuggestionItem[]> => {
      const employees = await getEmployees(query);

      // AI Agent always at top when enabled and matches query
      const normalizedQuery = query.toLowerCase();
      const aiMatches =
        aiEnabled &&
        (!query || 'ai agent'.includes(normalizedQuery) || aiAgentLabel.toLowerCase().includes(normalizedQuery));
      return aiMatches ? [aiAgentItem, ...employees] : employees;
    },

    render: () => {
      let component: ReactRenderer<MentionSuggestionListRef> | null = null;
      let popup: Instance[] | null = null;

      return {
        onStart: (props: SuggestionProps<MentionSuggestionItem, MentionNodeAttrs>) => {
          callbacks?.onActiveChange?.(true);

          component = new ReactRenderer(MentionSuggestionList, {
            props: { ...props, loading: false },
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          });
        },

        onUpdate: (props: SuggestionProps<MentionSuggestionItem, MentionNodeAttrs>) => {
          component?.updateProps({ ...props, loading: false });

          if (popup?.[0] && props.clientRect) {
            popup[0].setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect,
            });
          }
        },

        onKeyDown: (props: { event: KeyboardEvent }) => {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },

        onExit: () => {
          callbacks?.onActiveChange?.(false);
          popup?.[0]?.destroy();
          component?.destroy();
        },
      };
    },
  };
}
